import React, { useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Workspace-aware navigation helpers.
//
// Every authenticated app URL is prefixed with the current workspace's UUID
// (e.g. /<uuid>/dashboard). Rather than hand-build that prefix at ~200 call
// sites, components keep writing plain paths ("/dashboard", "/edit-agent/5")
// and these helpers inject the current workspace UUID automatically.
//
// A short allowlist of GLOBAL paths is left untouched — they intentionally
// live outside any workspace: public auth pages, the /admin portal, first-run
// onboarding, and billing (which is billing-account level, spanning workspaces).

const GLOBAL_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/admin",
];

// Exact paths that must stay global even though their prefix is workspace-scoped
// (e.g. /settings/* is scoped, but /settings/billing is account-level).
const GLOBAL_EXACT = ["/settings/billing"];

export function isGlobalPath(path) {
  if (typeof path !== "string") return true;
  if (/^([a-z]+:)?\/\//i.test(path)) return true; // http(s)://, //host
  if (/^(mailto:|tel:)/i.test(path)) return true;
  if (!path.startsWith("/")) return true; // relative/hash — leave untouched
  // Compare the path only — a query string or hash must not change whether a
  // path is global (e.g. "/settings/billing?upgrade=1" is exact-matched
  // against "/settings/billing", not compared as one literal string).
  const pathOnly = path.split(/[?#]/)[0];
  if (GLOBAL_EXACT.includes(pathOnly)) return true;
  return GLOBAL_PREFIXES.some((p) => pathOnly === p || pathOnly.startsWith(p + "/"));
}

// Prefix an app path with the workspace UUID, unless it's global, already
// prefixed, a number (navigate(-1)), or there's no workspace yet.
export function withWorkspace(to, uuid) {
  if (!uuid) return to;
  if (typeof to === "number") return to;

  if (to && typeof to === "object") {
    const pathname = to.pathname;
    if (
      typeof pathname === "string" &&
      !isGlobalPath(pathname) &&
      pathname !== `/${uuid}` &&
      !pathname.startsWith(`/${uuid}/`)
    ) {
      return { ...to, pathname: `/${uuid}${pathname}` };
    }
    return to;
  }

  if (typeof to !== "string") return to;
  if (isGlobalPath(to)) return to;
  if (to === `/${uuid}` || to.startsWith(`/${uuid}/`)) return to;
  return `/${uuid}${to.startsWith("/") ? to : "/" + to}`;
}

// Drop-in replacement for react-router's useNavigate that auto-prefixes the
// current workspace UUID. Call sites keep using `navigate("/dashboard")`.
export function useWsNavigate() {
  const navigate = useNavigate();
  const { currentWorkspace } = useAuth();
  const uuid = currentWorkspace?.uuid;
  return useCallback(
    (to, opts) => navigate(withWorkspace(to, uuid), opts),
    [navigate, uuid]
  );
}

// Drop-in replacement for react-router's <Link> / <NavLink>.
export function WorkspaceLink({ to, ...rest }) {
  const { currentWorkspace } = useAuth();
  return <Link to={withWorkspace(to, currentWorkspace?.uuid)} {...rest} />;
}

export function WorkspaceNavLink({ to, ...rest }) {
  const { currentWorkspace } = useAuth();
  return <NavLink to={withWorkspace(to, currentWorkspace?.uuid)} {...rest} />;
}
